import React, { useEffect, useState } from "react";
import { Container, Row, Col, Card, Table } from "react-bootstrap";
import { useNavigate, useSearchParams } from 'react-router-dom';

// Bootstrap
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import "bootstrap/dist/css/bootstrap.min.css";

// App
import "../App.css";
import InputForm from "../components/InputForm";
import MonthlyCostRow from "../components/Rows/MonthlyCostRow";
import OneTimeCostRow from "../components/Rows/OneTimeCostRow";
import PriceComparisonRow from "../components/Rows/PriceComparisonRow";
import StatCard from "../components/StatCard";
import LoanViewRow from "../components/Rows/LoanViewRow";

const CalculatorPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Helper functions for URL parameters
  const getUrlParams = () => {
    const params = {};

    // Parse all URL parameters
    if (searchParams.get('propertytype')) params.propertyType = searchParams.get('propertytype');
    if (searchParams.get('price')) params.utgangspris = parseFloat(searchParams.get('price'));
    if (searchParams.get('taxvalue')) params.taxeringsvarde = parseFloat(searchParams.get('taxvalue'));
    if (searchParams.get('mortgagecert')) params.pantbrev = parseFloat(searchParams.get('mortgagecert'));
    if (searchParams.get('monthlycost')) params.monthlyCost = parseFloat(searchParams.get('monthlycost'));
    if (searchParams.get('operatingcost')) params.driftkostnad = parseFloat(searchParams.get('operatingcost'));
    if (searchParams.get('debttoincomeHigh')) params.isDebtToIncomeHigh = searchParams.get('debttoincomeHigh') === 'true';
    // Backward compatibility for old URL parameters
    // Backward compatibility for old URL parameters
    if (searchParams.get('operating')) params.driftkostnad = parseFloat(searchParams.get('operating'));
    if (searchParams.get('driftkostnad')) params.driftkostnad = parseFloat(searchParams.get('driftkostnad'));
    if (searchParams.get('avgift')) params.avgift = parseFloat(searchParams.get('avgift'));
    if (searchParams.get('downpayment')) params.egenInsats = searchParams.get('downpayment');
    if (searchParams.get('mortgagerate')) params.mortgageInterestRate = parseFloat(searchParams.get('mortgagerate'));
    if (searchParams.get('amortization')) params.amortering = searchParams.get('amortization');

    return params;
  };

  const updateUrl = (newFormData, newPercentageValue, newActiveTab, newAmortizationTab, newAmortizationPercentage, manuallyEdited) => {
    const params = new URLSearchParams();
    
    // Add form data to URL (only add non-default values to keep URL clean)
    if (newFormData.propertyType && newFormData.propertyType !== "villa") params.set('propertytype', newFormData.propertyType);
    if (newFormData.utgangspris && newFormData.utgangspris !== 5000000) params.set('price', newFormData.utgangspris);
    // Only include taxvalue in URL if it was originally in the URL
    if (wasTaxvalueInUrl && newFormData.taxeringsvarde) params.set('taxvalue', newFormData.taxeringsvarde);
    // Only include pantbrev in URL if it was originally in the URL
    if (wasPantbrevInUrl && newFormData.pantbrev) params.set('mortgagecert', newFormData.pantbrev);
    // Both villa and bostadsrätt use operatingcost for driftkostnad
    if (newFormData.driftkostnad && newFormData.driftkostnad !== 0) params.set('operatingcost', newFormData.driftkostnad);
    // For bostadsrätt only: include monthlycost (avgift)
    if (newFormData.propertyType === "bostadsratt" && newFormData.monthlyCost && newFormData.monthlyCost !== 3000) params.set('monthlycost', newFormData.monthlyCost);
    // For villa: never include monthlycost in URL (only use operatingcost)
    if (newFormData.isDebtToIncomeHigh) params.set('debttoincomehigh', newFormData.isDebtToIncomeHigh);
    if (newFormData.egenInsats && manuallyEdited) params.set('downpayment', newFormData.egenInsats);
    if (newFormData.mortgageInterestRate && newFormData.mortgageInterestRate !== 2.25) params.set('mortgagerate', newFormData.mortgageInterestRate);
    if (newFormData.amortering) params.set('amortization', newFormData.amortering);
    
    // Add state parameters (only add non-default values)
    if (newPercentageValue && newPercentageValue !== 15) params.set('percentage', newPercentageValue);
    if (newActiveTab && newActiveTab !== "percentage") params.set('downpaymenttype', newActiveTab);
    if (newAmortizationTab && newAmortizationTab !== "percentage") params.set('amortizationtype', newAmortizationTab);
    if (newAmortizationPercentage && newAmortizationPercentage !== 2.0) params.set('amortizationpct', newAmortizationPercentage);
    
    // Update URL without reloading page
    setSearchParams(params);
  };

  // Check if parameters were in original URL
  const wasTaxvalueInUrl = searchParams.get('taxvalue') !== null;
  const wasPantbrevInUrl = searchParams.get('mortgagecert') !== null;
  const wasMonthlyCostInUrl = searchParams.get('monthlycost') !== null;
  const wasDriftKostnadInUrl = searchParams.get('operatingcost') !== null;

  // Initialize state from URL parameters or defaults
  const urlParams = getUrlParams();

  // Get property type early so we can use it below
  const propertyType = urlParams.propertyType || "villa";

  // Determine if critical parameters are missing
  // For villa: only driftkostnad (operatingcost) matters
  // For bostadsrätt: both monthlycost (avgift) and operatingcost (driftkostnad) matter
  const isMonthlyCostMissing = propertyType === "bostadsratt" && !wasMonthlyCostInUrl;
  const isDriftKostnadMissing = !wasDriftKostnadInUrl;
  
  // Track if this is a shared calculator load
  useEffect(() => {
    const hasUrlParams = Object.keys(urlParams).length > 0;
    if (hasUrlParams && window.plausible) {
      window.plausible('Shared Calculator Loaded', {
        props: {
          has_price: !!urlParams.utgangspris,
          has_rate: !!urlParams.mortgageInterestRate,
          param_count: Object.keys(urlParams).length
        }
      });
    }
  }, []); // Only run once on mount

  const utgangspris = urlParams.utgangspris || 5000000;

  // taxeringsvärde default depends on property type
  let defaultTaxeringsvarde = 3702000;
  if (!urlParams.taxeringsvarde) {
    if (propertyType === "villa" && utgangspris) {
      defaultTaxeringsvarde = utgangspris * 0.8;
    } else if (propertyType === "bostadsrätt") {
      defaultTaxeringsvarde = 0;
    }
  }

  const [formData, setFormData] = useState({
    propertyType: propertyType,
    utgangspris: utgangspris,
    taxeringsvarde: urlParams.taxeringsvarde || defaultTaxeringsvarde,
    pantbrev: urlParams.pantbrev || 0,
    // For villa: no monthlyCost (use driftkostnad only). For bostadsrätt: monthlyCost is avgift
    monthlyCost: propertyType === "villa" ? 0 : (urlParams.monthlyCost || urlParams.avgift || 3000),
    // Both villa and bostadsrätt use driftkostnad (from operatingcost parameter)
    driftkostnad: urlParams.driftkostnad || (propertyType === "villa" ? 3000 : 0),
    isDebtToIncomeHigh: urlParams.isDebtToIncomeHigh || false,
    egenInsats: urlParams.egenInsats || "",
    mortgageInterestRate: urlParams.mortgageInterestRate || 2.25,
    amortering: urlParams.amortering || "",
  });

  const [percentageValue, setPercentageValue] = useState(() => {
    return parseFloat(searchParams.get('percentage')) || 10;
  });
  
  const [activeTab, setActiveTab] = useState(() => {
    return searchParams.get('downpaymenttype') || "percentage";
  });
  
  const [isEgenInsatsManuallyEdited, setIsEgenInsatsManuallyEdited] = useState(() => {
    return searchParams.get('downpayment') !== null; // If there's a manual downpayment value in URL, mark as manually edited
  });
  
  const [amortizationTab, setAmortizationTab] = useState(() => {
    return searchParams.get('amortizationtype') || "percentage";
  });
  
  const [amortizationPercentage, setAmortizationPercentage] = useState(() => {
    return parseFloat(searchParams.get('amortizationpct')) || 2.0;
  });

  // Detect if taxeringsvärde is missing but other params are present
  const isTaxeringsvardeMissing = !urlParams.taxeringsvarde && (urlParams.propertyType || urlParams.utgangspris || urlParams.monthlyCost);

  // Check if taxeringsvärde and pantbrev are missing from original URL (for OneTimeCostRow warning)
  const isTaxeringsvardeMissingFromUrl = !searchParams.get('taxvalue');
  const isPantbrevMissingFromUrl = !searchParams.get('mortgagecert');

  const [isInputFormOpen, setIsInputFormOpen] = useState(false);
  const [isPartiallyExpanded, setIsPartiallyExpanded] = useState(false); // Starts collapsed
  const [highlightField, setHighlightField] = useState(null); // Track which field to highlight

  const [doughnutData, setDoughnutData] = useState([]);

  const [oneTimeCostDetailData, setOneTimeCostDetailData] = useState([]);
  const [oneTimeCost, setOneTimeCost] = useState([]);

  const [details, setDetails] = useState({});
  const [monthlyCostDetailData, setMonthlyCostDetailData] = useState([]);

  // 5-year revaluation rule (April 1, 2026)
  const [purchaseDate, setPurchaseDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0]; // YYYY-MM-DD format
  });
  const [lastRevaluationDate, setLastRevaluationDate] = useState(null);
  const [revaluationWarning, setRevaluationWarning] = useState('');

  const handleTabChange = (key) => {
    setActiveTab(key);
    if (key === "percentage") {
      setIsEgenInsatsManuallyEdited(false);
    }
  };

  const handleEgenInsatsChange = (value) => {
    setFormData({
      ...formData,
      egenInsats: value,
    });
    if (activeTab === "fixed" && value !== "") {
      setIsEgenInsatsManuallyEdited(true);
    }
  };

  const resetEgenInsats = () => {
    setFormData({
      ...formData,
      egenInsats: "",
    });
    setIsEgenInsatsManuallyEdited(false);
  };

  const validateRevaluation = () => {
    const dateToCompare = lastRevaluationDate ? new Date(lastRevaluationDate) : new Date(purchaseDate);
    const today = new Date();
    const daysSince = (today - dateToCompare) / (1000 * 60 * 60 * 24);
    const yearsSince = daysSince / 365.25;

    if (yearsSince < 5) {
      setRevaluationWarning(`Omvärdering kan endast genomföras var 5:e år. Nästa möjliga datum: ${getNextRevaluationDate(dateToCompare)}`);
      return false;
    }
    setRevaluationWarning('');
    return true;
  };

  const getNextRevaluationDate = (date) => {
    const nextDate = new Date(date);
    nextDate.setFullYear(nextDate.getFullYear() + 5);
    return nextDate.toISOString().split('T')[0];
  };

  const handleOpenSettings = (fieldToHighlight) => {
    // Check if settings are already open
    const isAlreadyOpen = isTaxeringsvardeMissing ? isPartiallyExpanded : isInputFormOpen;

    // Set the appropriate state based on whether taxeringsvärde is missing
    if (isTaxeringsvardeMissing) {
      setIsPartiallyExpanded(true);
    } else {
      setIsInputFormOpen(true);
    }
    setHighlightField(fieldToHighlight || null);
    setActiveTab("percentage");
    setPercentageValue(15);

    // Scroll to highlighted field if already open, or after a slight delay if opening
    if (fieldToHighlight) {
      const scrollDelay = isAlreadyOpen ? 0 : 300; // Wait for settings to open before scrolling
      setTimeout(() => {
        const highlightedElement = document.querySelector('.field-highlight');
        if (highlightedElement) {
          highlightedElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, scrollDelay);

      // Clear highlight after animation completes (1s delay + 0.8s animation)
      setTimeout(() => setHighlightField(null), 1800);
    }
  };

  const formatCurrency = (value) => {
    if (isNaN(value)) return value;
    return new Intl.NumberFormat("sv-SE", { style: "currency", currency: "SEK" })
      .format(value)
      .replace("kr", "")
      .trim();
  };

  const formatPriceForShare = (price) => {
    if (price >= 1000000) {
      const millions = price / 1000000;
      return `${millions.toFixed(1).replace('.0', '')}mlj kr`;
    } else if (price >= 1000) {
      const thousands = price / 1000;
      return `${thousands.toFixed(0)}tkr`;
    }
    return `${formatCurrency(price)} kr`;
  };

  const calculateResults = () => {
    const utgangspris = parseFloat(formData.utgangspris) || 0;
    const propertyType = formData.propertyType || "villa";
    // Both villa and bostadsrätt use operatingcost (driftkostnad) parameter
    const driftkostnad = parseFloat(formData.driftkostnad) || 0;
    // For bostadsrätt: monthlyCost is avgift (association fee)
    const avgift = propertyType === "bostadsratt" ? parseFloat(formData.monthlyCost) || 0 : 0;
    const totalOperatingCost = avgift + driftkostnad; // Total monthly cost (avgift + driftkostnad for bostadsrätt, or just driftkostnad for villa)
    const mortgageInterestRate = parseFloat(formData.mortgageInterestRate) / 100 || 0;

    const egenInsats =
      activeTab === "percentage"
        ? (utgangspris * (parseFloat(percentageValue) || 0)) / 100
        : parseFloat(formData.egenInsats) || (utgangspris * 0.10); // Default to 15% if not manually set

    const remainingAmount = Math.max(utgangspris - egenInsats, 0); // Loan amount
    const interestCost = (remainingAmount * mortgageInterestRate) / 12; // Monthly interest cost
    
    // Calculate belåningsgrad (Loan-to-Value ratio)
    const ltvRatio = utgangspris > 0 ? (remainingAmount / utgangspris) * 100 : 0;
    
    // Get debt-to-income threshold from user selection
    const isDebtToIncomeHigh = formData.isDebtToIncomeHigh || false;
    
    // Swedish amortization rules (April 1, 2026)
    // LTV-based amortization requirement
    let ltvAmortizationRate = 0;
    if (ltvRatio > 70) {
      ltvAmortizationRate = 0.02; // 2% per year
    } else if (ltvRatio > 50) {
      ltvAmortizationRate = 0.01; // 1% per year
    } else {
      ltvAmortizationRate = 0; // No amortization required
    }

    // Total required amortization rate (LTV-based only; high debt-to-income rule removed as of April 1, 2026)
    const totalRequiredAmortizationRate = ltvAmortizationRate;
    
    const calculatedAmortering = (remainingAmount * totalRequiredAmortizationRate) / 12; // Monthly amortization
    
    // Determine final amortization based on tab selection
    let finalAmortering;
    if (amortizationTab === "percentage") {
      finalAmortering = (remainingAmount * parseFloat(amortizationPercentage)) / 100 / 12;
    } else {
      // Use user input if explicitly set, otherwise use calculated value
      const userInput = parseFloat(formData.amortering);
      finalAmortering = !isNaN(userInput) && formData.amortering !== "" ? userInput : calculatedAmortering;
    }
    
    const userAmortering = finalAmortering;

    const taxeringsvarde = parseFloat(formData.taxeringsvarde) || 0;
    const pantbrev = parseFloat(formData.pantbrev) || 0;

    // For Bostadsrätt, no pantbrev or lagfart costs
    const nyaPantbrev = propertyType === "villa" ? Math.max(remainingAmount - pantbrev, 0) : 0;
    const kostnadNyaPantbrev = propertyType === "villa" && nyaPantbrev > 0 ? nyaPantbrev * 0.02 + 375 : 0;
    const kostnadLagfart = propertyType === "villa" ? Math.max(utgangspris, taxeringsvarde) * 0.015 + 825 : 0;

    // Calculate property tax with 2026 caps (April 1, 2026)
    let propertyTax = 0;
    if (propertyType === "villa" && taxeringsvarde > 0) {
      const annualTax = Math.min(taxeringsvarde * 0.0075, 10425);
      propertyTax = annualTax / 12;
    } else if (propertyType === "bostadsratt" && taxeringsvarde > 0) {
      const annualTax = Math.min(taxeringsvarde * 0.003, 1564);
      propertyTax = annualTax / 12;
    }

    // Monthly cost - filter out amortization if it's 0
    const monthlyCostData = [
      { name: "Ränta", value: formatCurrency(interestCost) }
    ];

    // Add property tax if applicable
    if (propertyTax > 0) {
      monthlyCostData.push({ name: "Fastighetsskatt", value: formatCurrency(propertyTax) });
    }

    // Only add amortization row if it's greater than 0
    if (userAmortering > 0) {
      monthlyCostData.push({ name: "Amortering", value: formatCurrency(userAmortering) });
    }

    // Add operating costs (varies by property type)
    if (propertyType === "villa") {
      monthlyCostData.push({ name: "Driftkostnad", value: formatCurrency(driftkostnad) });
    } else {
      // For bostadsrätt, show both avgift and driftkostnad if they exist
      if (avgift > 0) {
        monthlyCostData.push({ name: "Månadsavgift", value: formatCurrency(avgift) });
      }
      if (driftkostnad > 0) {
        monthlyCostData.push({ name: "Driftkostnad", value: formatCurrency(driftkostnad) });
      }
    }

    setMonthlyCostDetailData(monthlyCostData)

    // One time cost - only show for Villa
    if (propertyType === "villa") {
      setOneTimeCostDetailData([
      { name: "Nya pantbrev", value: formatCurrency(nyaPantbrev), description: "(Köpeskilling - Kontantinsats) - Befintliga pantbrev" },
      { name: "Stämpelskatt", value: formatCurrency(kostnadNyaPantbrev), description: "Kostnad för nya pantbrev: 2% av pantbrevsbeloppet + 375 kr" },
      { name: "Lagfartskostnad", value: formatCurrency(kostnadLagfart), description: "1.5% av köpeskilling eller taxeringsvärde (det som är högst) + 825 kr" },
      ])

      setOneTimeCost([
        { name: "Stämpelskatt", value: kostnadNyaPantbrev, color: "#003D7A" },
        { name: "Lagfart", value: kostnadLagfart, color: "#4A90E2" }
      ])
    } else {
      // For Bostadsrätt, no one-time costs
      setOneTimeCostDetailData([])
      setOneTimeCost([])
    }

    // Doughnut chart data - filter out amortization if it's 0
    const doughnutChartData = [
      { name: "Räntekostnad", value: interestCost, color: "#003D7A" }
    ];

    // Only add amortization to chart if it's greater than 0
    if (userAmortering > 0) {
      doughnutChartData.push({ name: "Amortering", value: userAmortering, color: "#4A90E2" });
    }

    // Add property tax to chart if applicable
    if (propertyTax > 0) {
      doughnutChartData.push({ name: "Fastighetsskatt", value: propertyTax, color: "#F39C12" });
    }

    // Add operating costs to chart (varies by property type)
    if (propertyType === "villa") {
      doughnutChartData.push({ name: "Driftkostnad", value: driftkostnad, color: "#8BA3B8" });
    } else {
      // For bostadsrätt, show both avgift and driftkostnad if they exist
      if (avgift > 0) {
        doughnutChartData.push({ name: "Månadsavgift", value: avgift, color: "#8BA3B8" });
      }
      if (driftkostnad > 0) {
        doughnutChartData.push({ name: "Driftkostnad", value: driftkostnad, color: "#B0BFD4" });
      }
    }

    setDoughnutData(doughnutChartData);

    setDetails({
      propertyType,
      utgangspris,
      avgift,
      driftkostnad,
      monthlyCost: formData.monthlyCost,
      taxeringsvarde,
      pantbrev,
      nyaPantbrev,
      kostnadNyaPantbrev,
      kostnadLagfart,
      propertyTax,
      ltvRatio,
      ltvAmortizationRate,
      totalRequiredAmortizationRate,
      calculatedAmortering,
      userAmortering,
    });
  };


  // Handle responsive layout based on window width
  useEffect(() => {
    const updateLayout = () => {
      const windowWidth = window.innerWidth;
      const mainContent = document.querySelector('.flex-grow-1');
      const chartRows = document.querySelectorAll('.chart-row');
      
      // Handle chart layout
      if (mainContent) {
        const contentWidth = mainContent.offsetWidth;
        chartRows.forEach(row => {
          if (contentWidth < 800 || windowWidth <= 768) {
            row.classList.add('force-vertical');
          } else {
            row.classList.remove('force-vertical');
          }
        });
      }
    };

    updateLayout();
    window.addEventListener('resize', updateLayout);
    
    return () => {
      window.removeEventListener('resize', updateLayout);
    };
  }, []);

  useEffect(() => {
    calculateResults();
    
    // Track calculator usage when significant values change
    const trackCalculatorUsage = () => {
      if (window.plausible && formData.utgangspris > 0) {
        const priceRange = formData.utgangspris >= 10000000 ? '10M+' : 
                          formData.utgangspris >= 5000000 ? '5-10M' :
                          formData.utgangspris >= 2000000 ? '2-5M' :
                          formData.utgangspris >= 1000000 ? '1-2M' : 'Under 1M';
        
        window.plausible('Calculator Usage', {
          props: {
            price_range: priceRange,
            interest_rate: formData.mortgageInterestRate,
            down_payment_type: activeTab,
            amortization_type: amortizationTab
          }
        });
      }
    };

    // Debounce tracking to avoid too many events
    const timeoutId = setTimeout(trackCalculatorUsage, 2000);
    return () => clearTimeout(timeoutId);
  }, [formData, activeTab, percentageValue, amortizationTab, amortizationPercentage]);

  // Update URL when form data or state changes
  useEffect(() => {
    updateUrl(formData, percentageValue, activeTab, amortizationTab, amortizationPercentage, isEgenInsatsManuallyEdited);
  }, [formData, percentageValue, activeTab, amortizationTab, amortizationPercentage, isEgenInsatsManuallyEdited]);

  // Generate complete URL with all parameters for sharing
  const generateShareUrl = () => {
    const params = new URLSearchParams();

    // Include parameters only if non-default (keep URLs clean)
    params.set('propertytype', formData.propertyType);
    if (formData.utgangspris && formData.utgangspris !== 5000000) params.set('price', formData.utgangspris);
    if (formData.taxeringsvarde && formData.taxeringsvarde !== 0) params.set('taxvalue', formData.taxeringsvarde);
    if (formData.pantbrev && formData.pantbrev !== 0) params.set('mortgagecert', formData.pantbrev);
    // Only include monthlycost for bostadsrätt if non-default
    if (formData.propertyType === "bostadsratt" && formData.monthlyCost && formData.monthlyCost !== 3000) params.set('monthlycost', formData.monthlyCost);
    // Only include driftkostnad if non-default
    if (formData.driftkostnad && formData.driftkostnad !== 0) params.set('operatingcost', formData.driftkostnad);
    if (formData.egenInsats && isEgenInsatsManuallyEdited) params.set('downpayment', formData.egenInsats);
    if (formData.mortgageInterestRate && formData.mortgageInterestRate !== 2.25) params.set('mortgagerate', formData.mortgageInterestRate);
    if (formData.amortering) params.set('amortization', formData.amortering);
    if (formData.isDebtToIncomeHigh) params.set('debttoincomehigh', formData.isDebtToIncomeHigh);

    // Include state parameters only if non-default
    if (percentageValue && percentageValue !== 10) params.set('percentage', percentageValue);
    if (activeTab && activeTab !== "percentage") params.set('downpaymenttype', activeTab);
    if (amortizationTab && amortizationTab !== "percentage") params.set('amortizationtype', amortizationTab);
    if (amortizationPercentage && amortizationPercentage !== 2.0) params.set('amortizationpct', amortizationPercentage);

    return `${window.location.origin}/calculator?${params.toString()}`;
  };

  // Handle share button click
  const handleShare = async () => {
    const shareUrl = generateShareUrl();
    const formattedPrice = formatPriceForShare(formData.utgangspris);
    const interestRate = formData.mortgageInterestRate;
    
    const shareData = {
      title: 'BoendeBeräkning.se - Bostadskalkylator',
      text: `Kolla in min bostadskalkyl för bostad på ${formattedPrice} med ${interestRate}% ränta - beräkna dina kostnader för bostadsköp i Sverige`,
      url: shareUrl
    };

    // Track share event in Plausible
    if (window.plausible) {
      window.plausible('Share Calculator', {
        props: {
          price: formData.utgangspris,
          interest_rate: formData.mortgageInterestRate,
          method: 'share_button'
        }
      });
    }

    try {
      // Check if native sharing is supported
      if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
        await navigator.share(shareData);
        // Track successful native share
        if (window.plausible) {
          window.plausible('Share Success', { props: { method: 'native' } });
        }
      } else {
        // Fallback: copy URL to clipboard
        await navigator.clipboard.writeText(shareUrl);
        alert('Länken har kopierats till urklipp!');
        // Track successful clipboard copy
        if (window.plausible) {
          window.plausible('Share Success', { props: { method: 'clipboard' } });
        }
      }
    } catch (error) {
      // Fallback if clipboard also fails
      console.error('Error sharing:', error);
      prompt('Kopiera denna länk för att dela:', shareUrl);
      // Track share error
      if (window.plausible) {
        window.plausible('Share Error', { props: { method: 'fallback' } });
      }
    }
  };

  // Handle restart button - navigate to wizard
  const handleRestart = () => {
    navigate('/');

    // Track restart action
    if (window.plausible) {
      window.plausible('Calculator Restart', {
        props: {
          from_price: formData.utgangspris,
          method: 'restart_button'
        }
      });
    }
  };

  // Generate monthly cost sparkline data (simulated trend over 12 months)
  const generateMonthlySparkline = () => {
    const baseValue = (() => {
      if (details && details.userAmortering !== undefined) {
        return (details.userAmortering || 0) + (details.avgift || 0) + (details.driftkostnad || 0) + ((details.utgangspris - (
          activeTab === "percentage"
            ? (details.utgangspris || 0) * (percentageValue / 100)
            : (parseFloat(formData.egenInsats) || (details.utgangspris || 0) * 0.15)
        )) * (parseFloat(formData.mortgageInterestRate) / 100) / 12);
      }
      return 0;
    })();

    // Create 12 data points with slight variation to simulate monthly fluctuation
    return Array.from({ length: 12 }, (_, i) => ({
      value: baseValue + (Math.random() - 0.5) * (baseValue * 0.02), // 2% variation
    }));
  };

  // Generate upfront cost sparkline data (flat trend since it's a one-time cost)
  const generateUpfrontSparkline = () => {
    const baseValue = (details.kostnadNyaPantbrev || 0) + (details.kostnadLagfart || 0);

    // Create 10 data points that stay relatively flat
    return Array.from({ length: 10 }, () => ({
      value: baseValue + (Math.random() - 0.5) * (baseValue * 0.01), // 1% variation
    }));
  };

  // Generate breakdown data for monthly cost stacked progress bar
  const generateMonthlyCostBreakdown = () => {
    const ranta = monthlyCostDetailData.find(item => item.name === 'Ränta');
    const amortering = monthlyCostDetailData.find(item => item.name === 'Amortering');
    const driftkostnad = monthlyCostDetailData.find(item => item.name === 'Driftkostnad');
    const avgift = monthlyCostDetailData.find(item => item.name === 'Avgift' || item.name === 'Månadsavgift');

    const parseValue = (val) => {
      if (!val) return 0;
      return parseFloat(val.replace(/[^0-9.,]/g, '').replace(',', '.')) || 0;
    };

    const rantaVal = parseValue(ranta?.value);
    const amortVal = parseValue(amortering?.value);
    const driftVal = parseValue(driftkostnad?.value);
    const avgiftVal = parseValue(avgift?.value);
    const total = rantaVal + amortVal + driftVal + avgiftVal;

    if (total === 0) return [];

    return [
      {
        name: 'Ränta',
        value: rantaVal,
        percentage: (rantaVal / total) * 100,
        color: '#003D7A',
      },
      {
        name: 'Amortering',
        value: amortVal,
        percentage: (amortVal / total) * 100,
        color: '#4A90E2',
      },
      {
        name: avgift?.name || 'Avgift',
        value: avgiftVal,
        percentage: (avgiftVal / total) * 100,
        color: '#8BA3B8',
      },
      {
        name: 'Driftkostnad',
        value: driftVal,
        percentage: (driftVal / total) * 100,
        color: '#B0BFD4',
      },
    ].filter(item => item.percentage > 0);
  };

  // Generate breakdown data for one-time cost stacked progress bar
  const generateOneTimeCostBreakdown = () => {
    const pantbrevVal = details.kostnadNyaPantbrev || 0;
    const lagfartVal = details.kostnadLagfart || 0;
    const total = pantbrevVal + lagfartVal;

    if (total === 0) return [];

    return [
      {
        name: 'Stämpelskatt',
        value: pantbrevVal,
        percentage: (pantbrevVal / total) * 100,
        color: '#003D7A',
      },
      {
        name: 'Lagfart',
        value: lagfartVal,
        percentage: (lagfartVal / total) * 100,
        color: '#4A90E2',
      },
    ].filter(item => item.percentage > 0);
  };

  return (
    <Container fluid className="d-flex flex-column min-vh-100 mx-auto responsive-container">
      {/* Main Content (Expands to Fill Space) */}
      <main className="p-3 flex-grow-1 d-flex flex-column" role="main" itemScope itemType="https://schema.org/WebApplication">
          
          <header role="banner">
            {/* Title and Action Buttons Row */}
            <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap">
              <h1 className="me-3 mb-2 mb-md-0" itemProp="name" style={{ minWidth: '50%' }}>
                Bostadskalkylator Sverige - Villa & Bostadsrätt | BoendeBeräkning.se
              </h1>
              <div className="d-flex gap-2">
                <button
                  className="btn btn-sm btn-outline-secondary d-flex align-items-center"
                  onClick={handleRestart}
                  title="Starta om med wizard"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"></path>
                    <path d="M21 3v5h-5"></path>
                    <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"></path>
                    <path d="M3 21v-5h5"></path>
                  </svg>
                  <span className="ms-1 d-none d-sm-inline">Starta om</span>
                </button>
                <button
                  className="btn btn-sm btn-navy d-flex align-items-center"
                  onClick={handleShare}
                  title="Dela kalkylator"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"></path>
                    <polyline points="16,6 12,2 8,6"></polyline>
                    <line x1="12" y1="2" x2="12" y2="15"></line>
                  </svg>
                  <span className="ms-1 d-none d-sm-inline">Dela</span>
                </button>
              </div>
            </div>

            {/* Collapsible Input Form Box */}
            <Card className="mb-4" style={{ border: '1px solid #E8EBF0', borderRadius: '12px' }}>
              <Card.Header
                onClick={() => {
                  if (isTaxeringsvardeMissing) {
                    setIsPartiallyExpanded(!isPartiallyExpanded);
                  } else {
                    setIsInputFormOpen(!isInputFormOpen);
                  }
                }}
                style={{
                  cursor: 'pointer',
                  backgroundColor: '#F5F7FA',
                  border: 'none',
                  padding: '1rem'
                }}
              >
                <div className="d-flex justify-content-between align-items-center">
                  <h5 style={{ marginBottom: 0, fontSize: '1rem', fontWeight: '600', color: '#2c3e50' }}>
                    Kalkylatorinställningar
                  </h5>
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    style={{
                      transform: (isTaxeringsvardeMissing ? isPartiallyExpanded : isInputFormOpen) ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.3s ease'
                    }}
                  >
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                </div>
              </Card.Header>
              {(isTaxeringsvardeMissing ? isPartiallyExpanded : isInputFormOpen) && (
                <Card.Body style={{ padding: '1.5rem' }}>
                  <InputForm
                    formData={formData}
                    setFormData={setFormData}
                    activeTab={activeTab}
                    handleTabChange={handleTabChange}
                    setActiveTab={setActiveTab}
                    percentageValue={percentageValue}
                    setPercentageValue={setPercentageValue}
                    details={details}
                    amortizationTab={amortizationTab}
                    setAmortizationTab={setAmortizationTab}
                    amortizationPercentage={amortizationPercentage}
                    setAmortizationPercentage={setAmortizationPercentage}
                    handleEgenInsatsChange={handleEgenInsatsChange}
                    isEgenInsatsManuallyEdited={isEgenInsatsManuallyEdited}
                    resetEgenInsats={resetEgenInsats}
                    highlightField={highlightField}
                  />
                </Card.Body>
              )}
            </Card>

            <Row className="mb-4">
              <Col md={12}>
                <section role="region" aria-label="Lån" itemScope itemType="https://schema.org/FinancialProduct">
                  <LoanViewRow
                    details={details}
                    monthlyCostDetailData={monthlyCostDetailData}
                    doughnutData={doughnutData}
                    downPaymentAmount={activeTab === "percentage" ? (formData.utgangspris * (percentageValue / 100)) : (parseFloat(formData.egenInsats) || (formData.utgangspris * 0.10))}
                    />
                </section>
              </Col>
            </Row>

            {/* Stat Cards Row */}
            <Row className="mb-4">
              <Col md={6} className="mb-3 mb-md-0">
                <StatCard
                  title="Totala månadskostnaden"
                  value={
                    details && details.userAmortering !== undefined
                      ? (details.userAmortering || 0) + (details.avgift || 0) + (details.driftkostnad || 0) + ((details.utgangspris - (
                          activeTab === "percentage"
                            ? (details.utgangspris || 0) * (percentageValue / 100)
                            : (parseFloat(formData.egenInsats) || (details.utgangspris || 0) * 0.15)
                        )) * (parseFloat(formData.mortgageInterestRate) / 100) / 12)
                      : 0
                  }
                  subtitle=""
                  breakdownData={generateMonthlyCostBreakdown()}
                />
              </Col>
              <Col md={6}>
                <StatCard
                  title="Totala engångskostnader"
                  value={(details.kostnadNyaPantbrev || 0) + (details.kostnadLagfart || 0)}
                  subtitle=""
                  breakdownData={generateOneTimeCostBreakdown()}
                  emptyMessage={(details.kostnadNyaPantbrev || 0) + (details.kostnadLagfart || 0) === 0 ? "Ingen pantbrev eller lagfart krävs för bostadsrätt." : undefined}
                />
              </Col>
            </Row>
          </header>
          
          <Row className="mb-4">
              <Col lg={6} md={12} className="mb-3 mb-md-0">
                <section role="region" aria-label="Månadskostnader" itemScope itemType="https://schema.org/FinancialProduct">
                  <MonthlyCostRow
                    details={details}
                    monthlyCostDetailData={monthlyCostDetailData}
                    doughnutData={doughnutData}
                    isMonthlyCostMissing={isMonthlyCostMissing}
                    isDriftKostnadMissing={isDriftKostnadMissing}
                    propertyType={formData.propertyType}
                    onOpenSettings={handleOpenSettings}
                  />
                </section>
              </Col>
              <Col lg={6} md={12} className="mb-3 mb-md-0">
                <section role="region" aria-label="Engångskostnader" itemScope itemType="https://schema.org/FinancialProduct">
                  <OneTimeCostRow
                    oneTimeCostDetailData={oneTimeCostDetailData}
                    oneTimeCost={oneTimeCost}
                    propertyType={formData.propertyType}
                    isTaxeringsvardeMissing={isTaxeringsvardeMissingFromUrl}
                    isPantbrevMissing={isPantbrevMissingFromUrl}
                    onOpenSettings={handleOpenSettings}
                  />
                </section>
              </Col>
          </Row>
          
          <section role="region" aria-label="Prisjämförelse" itemScope itemType="https://schema.org/ComparisonTable">
            <PriceComparisonRow
              formData={formData}
              details={details}
              activeTab={activeTab}
              percentageValue={percentageValue}
              amortizationTab={amortizationTab}
              amortizationPercentage={amortizationPercentage}
              setFormData={setFormData}
              setActiveTab={setActiveTab}
              setPercentageValue={setPercentageValue}
              setIsEgenInsatsManuallyEdited={setIsEgenInsatsManuallyEdited}
            />
          </section>
          {/* Footer (Stays at Bottom) */}
          <footer className="footer mt-auto">
            <div className="d-flex justify-content-between align-items-center flex-wrap">
              <div className="footer-links mb-1">
                <a href="/about.html" className="me-3 text-decoration-none">Om sidan</a>
                <a href="/contact.html" className="text-decoration-none">Kontakt</a>
              </div>
              <div className="footer-copyright">
                &copy; {new Date().getFullYear()} Paul Peelen. All Rights Reserved.
              </div>
            </div>
          </footer>
        </main>
    </Container>
  );
};

export default CalculatorPage;